import axios from "axios";

export type SegmentSource = {
    type: string,
    id: string,
    key: string,
    value: string,
    value_type: string
    os: string,
    browser: string,
    major: string,
    device: string,
    country: string,
    city: string,
};

export type Pixel = {
    id: string
    name: string
}

export type Event = {
    id: string
    name: string
}

export type Segment = {
    id: string
    name: string
}

export type Category = {
    id: string
    name: string
}

import {Vue, Component, Prop, Ref, Model} from "vue-property-decorator";

@Component({})
export default class SegmentSources extends Vue {
    @Prop() private type !: string
    @Prop() private error_message !: string

    @Ref() private form!: any

    @Prop() private events!: Array<Event>
    @Prop() private pixels!: Array<Pixel>
    @Prop() private segments!: Array<Segment>
    @Prop() private categories!: Array<Category>

    private oss = Array<string>()
    private ossVersions = {} as any
    private browsers = Array<string>()
    private browsersVersions = {} as any
    private devices = Array<string>()
    private countries = Array<string>()
    private cities = {} as any

    private dialog: boolean = false
    private type_name: string = ''
    private type_icon: string = ''
    private value_types: Array<any> = [
        {
            value: 'equals',
            text: 'Equals',
        },
        {
            value: 'contains',
            text: 'Contains',
        },
        {
            value: 'pattern',
            text: 'Pattern',
        },
    ]

    private rules: any = {
        event_key: [
            (v: string) => !!v || 'Key is required',
        ],
        id: [
            (v: string) => !!v || 'ID is required',
        ],
        event_value: [
            (v: string) => !!v || 'Value is required',
        ],
        os: [
            (v: string) => !!v || 'OS is required',
        ],
        browser: [
            (v: string) => !!v || 'Browser is required',
        ],
        device: [
            (v: string) => !!v || 'Device is required',
        ],
        country: [
            (v: string) => !!v || 'Country is required',
        ],
    }

    @Model()
    private sources!: Array<SegmentSource>

    private new_source!: any

    action(arg: any) {
        if (arg === undefined) {
            arg = this.new_source
        }
        this.new_source = arg
        switch (arg) {
            case 'pixel':
                this.sources.push({
                    type: 'pixel',
                    id: '',
                } as SegmentSource)
                this.$emit('change')
                this.dialog = false
                break
            case 'event':
                this.sources.push({
                    type: 'event',
                    id: '',
                    value_type: 'equals'
                } as SegmentSource)
                this.$emit('change')
                this.dialog = false
                break
            case 'page_url':
                this.sources.push({
                    type: 'page_url',
                    id: '',
                    value_type: 'contains'
                } as SegmentSource)
                this.$emit('change')
                this.dialog = false
                break
            case 'segment':
                this.sources.push({
                    type: 'segment',
                    id: '',
                } as SegmentSource)
                this.$emit('change')
                this.dialog = false
                break
            case 'os':
                this.dialog = false
                this.loadOSs(true)
                break
            case 'browser':
                this.dialog = false
                this.loadBrowsers(true)
                break
            case 'device':
                this.dialog = false
                this.loadDevices(true)
                break
            case 'location':
                this.dialog = false
                this.loadCountries(true)
                break
        }
    }

    created() {
        switch (this.type) {
            case 'union':
                this.type_name = 'Union'
                this.type_icon = 'mdi-set-all'
                break
            case 'inter':
                this.type_name = 'Intersection'
                this.type_icon = 'mdi-set-center'
                break
            case 'diff':
                this.type_name = 'Difference'
                this.type_icon = 'mdi-set-left'
        }
    }

    async loadBrowsers(push: boolean) {
        if (!this.browsers || this.browsers.length == 0) {
            let resp = await axios.get<Array<string>>('/api/v1/segments/browsers')
            this.browsers = resp.data
        }

        if (push) {
            this.sources.push({
                type: 'browser',
                browser: '',
                major: '',
            } as SegmentSource)
            this.$emit('change')
        }
    }

    loadBrowserVersions(source: SegmentSource, clearSelection: boolean) {
        if (clearSelection) source.major = ''
        axios.get<Array<string>>('/api/v1/segments/browsers/versions', {
            params: {
                browser: source.browser,
            }
        }).then((resp) => {
            let versions = this.browsersVersions[source.browser] || []
            if (versions && versions.length == 0 && resp.data && resp.data.length > 0) {
                this.$set(this.browsersVersions, source.browser, resp.data)
            }
        })
    }

    async loadOSs(push: boolean) {
        if (!this.oss || this.oss.length == 0) {
            let resp = await axios.get<Array<string>>('/api/v1/segments/oss')
            this.oss = resp.data
        }

        if (push) {
            this.sources.push({
                type: 'os',
                os: '',
                major: '',
            } as SegmentSource)
            this.$emit('change')
        }
    }

    loadOSVersions(source: SegmentSource, clearSelection: boolean) {
        if (clearSelection) source.major = ''
        axios.get<Array<string>>('/api/v1/segments/oss/versions', {
            params: {
                os: source.os,
            }
        }).then((resp) => {
            let versions = this.ossVersions[source.os] || []
            if (versions && versions.length == 0 && resp.data && resp.data.length > 0) {
                this.$set(this.ossVersions, source.os, resp.data)
            }
        })
    }

    async loadDevices(push: boolean) {
        if (!this.devices || this.devices.length == 0) {
            let resp = await axios.get<Array<string>>('/api/v1/segments/devices')
            this.devices = resp.data
        }

        if (push) {
            this.sources.push({
                type: 'device',
                device: '',
            } as SegmentSource)
            this.$emit('change')
        }
    }

    async loadCountries(push: boolean) {
        if (!this.countries || this.countries.length == 0) {
            let resp = await axios.get<Array<string>>('/api/v1/segments/countries')
            this.countries = resp.data
        }

        if (push) {
            this.sources.push({
                type: 'location',
                country: '',
                city: '',
            } as SegmentSource)
            this.$emit('change')
        }
    }

    loadCities(source: SegmentSource, clearSelection: boolean) {
        if (clearSelection) source.city = ''
        axios.get<Array<string>>('/api/v1/segments/countries/cities', {
            params: {
                country: source.country,
            }
        }).then((resp) => {
            let cities = this.cities[source.country] || []
            if (cities && cities.length == 0 && resp.data && resp.data.length > 0) {
                this.$set(this.cities, source.country, resp.data)
            }
        })
    }

    mounted() {
        this.loadMissingComponents(this.sources)
    }

    public loadMissingComponents(sources: Array<SegmentSource>) {
        if(!sources) return

        sources.forEach((source) => {
            switch (source.type) {
                case 'device':
                    this.loadDevices(false)
                    break
                case 'browser':
                    this.loadBrowsers(false)
                    if (source.browser != '') this.loadBrowserVersions(source, false)
                    break
                case 'os':
                    this.loadOSs(false)
                    if (source.os != '') this.loadOSVersions(source, false)
                    break
                case 'location':
                    this.loadCountries(false)
                    if (source.country != '') this.loadCities(source, false)
            }
        })
    }

    public validate(): boolean {
        return this.form.validate()
    }

    private remove(index: number) {
        this.sources.splice(index, 1)
        this.$emit('change')
    }

}

// 99 union 131 0 {"type":"pixel","id":"03548964-6172-4794-9973-b0fdc22d92a7"}
// 100 union 132 0 {"type":"event","id":"047c520e-f6e6-4dbd-bebc-0238e4ba1028","value_type":"equals","key":"key","value":"val"}
// 101 union 0 0 {"type":"page_url","id":"04c29009-d4ca-43ff-b64a-b2a6d8528228","key":"config.page_url","value_type":"contains","value":"debug"}
// 102 intersection 133 0 {"type":"location","country":"Ukraine","city":"","version":"Kyiv"}
// 103 intersection 134 0 {"type":"browser","browser":"Chrome","version":"91"}
// 104 intersection 135 0 {"type":"os","os":"Android","version":"11"}
// 105 difference 136 0 {"type":"device","device":"Apple iPad"}
// 106 difference 0 8 {"type":"segment","id":"0ee7b4e8-25ec-4183-a109-d4415063af74"}
// 107 difference 137 0 {"type":"location","country":"Azerbaijan","city":"","version":""}